import React, { useState, useEffect } from 'react';
import CommonModal from '@/components/ui/modals/CommonModal';
import { cn } from '@/lib/utils';
import { useSelector, useDispatch } from 'react-redux';
import CurrencyCodeAdd from '@/components/common/CurrencyCodeAdd';
import { getByTableIdOrderService } from '@/services/OrderServices';
import useOrderReceiptPrint from '@/hooks/useOrderReceiptPrint';
import validationErrors from '@/lib/validationErrors';
import { getGeneralSettingStoreAfterLoginService } from '@/services/AuthServices';
import { GeneralSettingList } from '@/constants/GeneralSettingConstant';
import { getReceiptPaperSettings } from '@/lib/receiptPaper';
import {
    formatReceiptDateTime,
    getReceiptItemName,
} from '@/lib/receiptRender';

const POSReceiptModal = ({ modelTitle, modalOpen, setModalOpen, receiptOrderId }) => {
    const { tableOrder, generalSetting } = useSelector((state) => state?.GeneralSetting);
    const { userData } = useSelector((state) => state?.Auth);
    const dispatch = useDispatch();
    const [kitchenModalOpen, setKitchenModalOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [orderData, setOrderData] = useState();
    const printCount = parseInt(generalSetting?.pos_invoice_print_count, 10) || 1;
    const { printRef, handlePrint: handleReceiptPrint } = useOrderReceiptPrint({
        copyCount: printCount,
        orderData,
        generalSetting,
        userData,
        receiptType: 'customer',
    });
    const { printRef: kitchenPrintRef, handlePrint: handleKitchenReceiptPrint } = useOrderReceiptPrint({
        copyCount: printCount,
        orderData,
        generalSetting,
        userData,
        receiptType: 'kitchen',
    });
    const paymentStatusOptionList = [
        { value: 1, label: 'Unpaid' },
        { value: 2, label: 'Paid' },
        { value: 3, label: 'Failed' },
        { value: 4, label: 'Waiting For Approval' },
    ];
    useEffect(() => {
        if (receiptOrderId && receiptOrderId != '') {
            getByIdOrderData(receiptOrderId);
        }
    }, [receiptOrderId]);

    const { receiptPaperWidthMm, receiptDialogStyle } = getReceiptPaperSettings(generalSetting);

    // Ensure generalSetting is loaded when modal opens
    useEffect(() => {
        if (modalOpen && (!generalSetting || !generalSetting.company_name)) {
            dispatch(getGeneralSettingStoreAfterLoginService({ key_list: GeneralSettingList })).catch(() => {});
        }
    }, [modalOpen, generalSetting, dispatch]);

    const handleModelClose = () => {
        setModalOpen(false);
    };

    const getItemVariations = (itemVariations) => {
        try {
            // Parse the itemVariations if it is a string
            const parsedItemVariations = typeof itemVariations === 'string' ? JSON.parse(itemVariations) : itemVariations;

            // Handle null or invalid data gracefully
            if (!parsedItemVariations || !parsedItemVariations._ids) {
                return null;
            }

            // Map over variations and generate JSX elements
            return parsedItemVariations._ids.map((element, index) => {
                const variationName = parsedItemVariations.name[index]; // Assuming 'name' holds variation name
                const variationAttributeName = parsedItemVariations.attribute_name[index]; // Assuming 'name' holds variation name
                return (
                    <span key={index}>
                        {variationAttributeName}: {variationName}
                        {index < parsedItemVariations._ids.length - 1 && ', '}
                    </span>
                );
            });
        } catch (error) {
            console.error('Error parsing item variations:', error);
            return null;
        }
    };

    const getItemExtra = (itemExtars) => {
        try {
            const parsedItemExtars = typeof itemExtars === 'string' ? JSON.parse(itemExtars) : itemExtars;

            if (!parsedItemExtars || !parsedItemExtars._ids) {
                return null;
            }

            return parsedItemExtars._ids.map((element, index) => {
                const extraName = parsedItemExtars.name[index];
                return (
                    <span key={index}>
                        {extraName}
                        {index < parsedItemExtars._ids.length - 1 && ', '}
                    </span>
                );
            });
        } catch (error) {
            console.error('Error parsing item variations:', error);
            return null;
        }
    };

    const getByIdOrderData = (receiptOrderId) => {
        setIsLoading(true);
        dispatch(getByTableIdOrderService(receiptOrderId))
            .then((res) => {
                setOrderData(res?.data?.data);
                setIsLoading(false);
            })
            .catch(({ message, errorData, statusCode }) => {
                validationErrors(errorData, message);
                setIsLoading(false);
            });
    };

    return (
        <>
        <CommonModal addonsclassName={'ff-modal info-modal'} modelHeaderHide={true} modelTitle={modelTitle} openModel={modalOpen} handleModelClose={handleModelClose}>
            <div className={cn('modal pos-receipt-modal', modalOpen && 'active')}>
                <div className="modal-dialog rounded-none" id="print" style={receiptDialogStyle}>
                    <div className="modal-header hidden-print">
                        <button type="button" className="modal-close flex items-center justify-center gap-1.5 py-2 px-4 rounded bg-[#FB4E4E]" onClick={() => setModalOpen(false)}>
                            <i className="lab lab-back-bold lab-font-size-16 text-white"></i>
                            <span className="text-xs leading-5 capitalize text-white">Close</span>
                        </button>
                        <div className="flex gap-2">
                            <button type="button" className="flex items-center justify-center gap-1.5 py-2 px-4 rounded bg-[#1AB759]" onClick={() => handleReceiptPrint()}>
                                <i className="lab lab-print-bold lab-font-size-16 text-white"></i>
                                <span className="text-xs leading-5 capitalize text-white">Print Receipt</span>
                            </button>
                            <button type="button" className="flex items-center justify-center gap-1.5 py-2 px-4 rounded bg-[#FF6B35]" onClick={() => setKitchenModalOpen(true)}>
                                <i className="lab lab-print-bold lab-font-size-16 text-white"></i>
                                <span className="text-xs leading-5 capitalize text-white">Kitchen Receipt</span>
                            </button>
                        </div>
                    </div>
                    <div className="modal-body" ref={printRef} data-receipt-width-mm={String(receiptPaperWidthMm)}>
                        {/* Company Info - Only on Customer Copy (First Receipt) */}
                        <div className="text-center pb-3.5 border-b border-dashed border-gray-400">
                            {generalSetting?.company_name && <h3 className="text-2xl font-bold mb-1">{generalSetting.company_name}</h3>}
                            {generalSetting?.company_address && generalSetting.company_address.trim() !== '' && (
                                <h4 className="text-sm font-normal mt-1">{generalSetting.company_address}</h4>
                            )}
                            {generalSetting?.company_phone && generalSetting.company_phone.trim() !== '' && (
                                <h5 className="text-sm font-normal mt-1">Tel: {generalSetting.company_phone}</h5>
                            )}
                            {generalSetting?.company_phone_2 && generalSetting.company_phone_2.trim() !== '' && (
                                <h5 className="text-sm font-normal mt-1">Tel: {generalSetting.company_phone_2}</h5>
                            )}
                        </div>
                        {/* Order Info - Order Number only on Customer Copy, Date on Both */}
                        {orderData?.order_serial_no && (
                            <table className="w-full my-1.5">
                                <tbody>
                                    <tr>
                                        <td className="text-xs text-left py-0.5 text-heading">Order #{orderData.order_serial_no}</td>
                                    </tr>
                                </tbody>
                            </table>
                        )}
                        {/* Date - Visible on Both Customer and Kitchen Copies */}
                        {orderData?.order_datetime && (
                            <table className="w-full my-1.5">
                                <tbody>
                                    <tr>
                                        <td className="text-xs text-left py-0.5 text-heading">Date: {formatReceiptDateTime(orderData.order_datetime)}</td>
                                    </tr>
                                </tbody>
                            </table>
                        )}
                        <table className="w-full">
                            <thead className="border-t border-b border-dashed border-gray-400">
                                <tr>
                                    <th scope="col" className="py-1 font-normal text-xs capitalize text-left text-heading w-8">
                                        qty
                                    </th>
                                    <th scope="col" className="py-1 font-normal text-xs capitalize flex items-center justify-between text-heading">
                                        <span>Item Description</span>
                                        <span className="price-header">Price</span>
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="border-b border-dashed border-gray-400">
                                {orderData?.order_items &&
                                    orderData?.order_items.length > 0 &&
                                    orderData?.order_items?.map((cart, index) => (
                                        <tr key={index}>
                                            <td className="text-left font-normal align-top py-1">
                                                <p className="text-xs leading-5 text-heading">{cart?.quantity}</p>
                                            </td>
                                            <td className="text-left font-normal align-top py-1">
                                                <div className="flex items-center justify-between receipt-item-row">
                                                    <h4 className="text-sm font-normal capitalize receipt-item-name">{getReceiptItemName(orderData, cart, index)}</h4>
                                                    <p className="text-xs leading-5 text-heading item-price">
                                                        <CurrencyCodeAdd price={cart.total_price} />
                                                    </p>
                                                </div>
                                                {cart.item_variations && cart.item_variations != '' && (
                                                    <p className="text-xs leading-5 font-normal text-heading max-w-[200px]">{getItemVariations(cart.item_variations)}</p>
                                                )}
                                                {cart.item_extras && cart.item_extras != '' && (
                                                    <p className="text-xs leading-5 font-normal text-heading max-w-[200px]">{getItemExtra(cart.item_extras)}</p>
                                                )}
                                                {cart.instruction && <p className="text-xs leading-5 font-normal text-heading max-w-[200px]">Instruction: {cart.instruction}</p>}
                                            </td>
                                        </tr>
                                    ))}
                            </tbody>
                        </table>
                        <div className="py-2 pl-7">
                            <table className="w-full">
                                <tbody>
                                    <tr>
                                        <td className="text-xs text-left py-0.5 uppercase text-heading">Subtotal:</td>
                                        <td className="text-xs text-right py-0.5 text-heading">
                                            <CurrencyCodeAdd price={orderData?.subtotal} />
                                        </td>
                                    </tr>
                                    <tr>
                                        <td className="text-xs text-left py-0.5 uppercase text-heading">Total VAT:</td>
                                        <td className="text-xs text-right py-0.5 text-heading">
                                            <CurrencyCodeAdd price={orderData?.total_tax} />
                                        </td>
                                    </tr>
                                    <tr>
                                        <td className="text-xs text-left py-0.5 uppercase text-heading">Discount:</td>
                                        <td className="text-xs text-right py-0.5 text-heading">
                                            <CurrencyCodeAdd price={orderData?.discount} />
                                        </td>
                                    </tr>
                                    <tr>
                                        <td className="text-xs text-left py-0.5 font-bold uppercase text-heading">Total:</td>
                                        <td className="text-xs text-right py-0.5 font-bold text-heading">
                                            <CurrencyCodeAdd price={orderData?.total} />
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                        <div className="text-xs py-2 border-t border-b border-dashed border-gray-400 text-heading">
                            <p>Payment Type: {orderData?.payment_method_id && orderData?.payment_gateway ? orderData?.payment_gateway?.name : 'Cash'}</p>
                        </div>
                        {/* Served by - visible on both customer and kitchen copies */}
                        <div className="text-center pt-2 pb-4">
                            <p className="text-base font-bold leading-6 capitalize text-heading">
                                Served by: {orderData?.waiter?.name || orderData?.waiter_name || userData?.name || userData?.email || 'N/A'}
                            </p>
                        </div>
                        {orderData?.token && <h4 className="py-2 capitalize text-xl font-bold text-center border-b border-dashed border-gray-400">Token #{orderData?.token}</h4>}
                        <div className="text-center pt-2 pb-4">
                            <p className="text-[11px] leading-[14px] capitalize text-heading">Thank You</p>
                            <p className="text-[11px] leading-[14px] capitalize text-heading">please Come Again</p>
                        </div>
                        <div className="flex flex-col items-end">
                            <h5 className="text-[8px] font-normal text-left w-[46px] leading-[10px]">Powered by</h5>
                            <h6 className="text-xs font-normal leading-4">TabanFood</h6>
                        </div>
                    </div>
                </div>
            </div>
        </CommonModal>
        {/* Kitchen Receipt Modal */}
        <CommonModal addonsclassName={'ff-modal info-modal'} modelHeaderHide={true} modelTitle={modelTitle} openModel={kitchenModalOpen} handleModelClose={() => setKitchenModalOpen(false)}>
                <div className={cn('modal pos-receipt-modal', kitchenModalOpen && 'active')}>
                    <div className="modal-dialog rounded-none" id="print" style={receiptDialogStyle}>
                        <div className="modal-header hidden-print">
                            <button type="button" className="modal-close flex items-center justify-center gap-1.5 py-2 px-4 rounded bg-[#FB4E4E]" onClick={() => setKitchenModalOpen(false)}>
                                <i className="lab lab-back-bold lab-font-size-16 text-white"></i>
                                <span className="text-xs leading-5 capitalize text-white">Close</span>
                            </button>
                            <button type="button" className="flex items-center justify-center gap-1.5 py-2 px-4 rounded bg-[#FF6B35]" onClick={() => handleKitchenReceiptPrint()}>
                                <i className="lab lab-print-bold lab-font-size-16 text-white"></i>
                                <span className="text-xs leading-5 capitalize text-white">Print Kitchen Receipt</span>
                            </button>
                        </div>
                        <div className="modal-body" ref={kitchenPrintRef} data-receipt-width-mm={String(receiptPaperWidthMm)}>
                            {/* Kitchen Receipt Header */}
                            <div className="text-center pb-3.5 border-b border-dashed border-gray-400">
                                <h3 className="text-2xl font-bold mb-1">Kitchen Receipt</h3>
                            </div>
                            {/* Date - Visible on Kitchen Copy */}
                            {orderData?.order_datetime && (
                                <table className="w-full my-1.5">
                                    <tbody>
                                        <tr>
                                            <td className="text-xs text-left py-0.5 text-heading">Date: {formatReceiptDateTime(orderData.order_datetime)}</td>
                                        </tr>
                                    </tbody>
                                </table>
                            )}
                            <table className="w-full">
                                <thead className="border-t border-b border-dashed border-gray-400">
                                    <tr>
                                        <th scope="col" className="py-1 font-normal text-xs capitalize text-left text-heading w-8">
                                            qty
                                        </th>
                                        <th scope="col" className="py-1 font-normal text-xs capitalize text-left text-heading">
                                            Item Description
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="border-b border-dashed border-gray-400">
                                    {orderData?.order_items &&
                                        orderData?.order_items.length > 0 &&
                                        orderData?.order_items?.map((cart, index) => (
                                            <tr key={index}>
                                                <td className="text-left font-normal align-top py-1">
                                                    <p className="text-xs leading-5 text-heading">{cart?.quantity}</p>
                                                </td>
                                                <td className="text-left font-normal align-top py-1">
                                                    <h4 className="text-sm font-normal capitalize receipt-item-name">{getReceiptItemName(orderData, cart, index)}</h4>
                                                    {cart.item_variations && cart.item_variations != '' && (
                                                        <p className="text-xs leading-5 font-normal text-heading max-w-[200px]">{getItemVariations(cart.item_variations)}</p>
                                                    )}
                                                    {cart.item_extras && cart.item_extras != '' && (
                                                        <p className="text-xs leading-5 font-normal text-heading max-w-[200px]">{getItemExtra(cart.item_extras)}</p>
                                                    )}
                                                    {cart.instruction && <p className="text-xs leading-5 font-normal text-heading max-w-[200px]">Instruction: {cart.instruction}</p>}
                                                </td>
                                            </tr>
                                        ))}
                                </tbody>
                            </table>
                            {/* Served by - visible on kitchen copy */}
                            <div className="text-center pt-2 pb-4">
                                <p className="text-base font-bold leading-6 capitalize text-heading">
                                    Served by: {orderData?.waiter?.name || orderData?.waiter_name || userData?.name || userData?.email || 'N/A'}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </CommonModal>
        </>
    );
};

export default POSReceiptModal;
